/**
 * ThinkingBlock Component
 *
 * Displays LLM reasoning/thinking in a Cursor-like UI:
 * - During streaming: "Thinking" label with a 3-line scrollable preview
 * - After completion: "Thought for Xs" with a collapsed chevron
 * - When expanded: shows the full thinking content
 */

import React, { useRef, useEffect, useState } from "react";
import { usePortal } from "../../../Portal/PortalContext";

export interface ThinkingBlockProps {
  /** The accumulated thinking text */
  thinkingContent: string;
  /** Whether thinking is still streaming */
  isThinking: boolean;
  /** Duration in seconds (set when thinking completes) */
  durationSeconds?: number;
}

export const ThinkingBlock: React.FC<ThinkingBlockProps> = ({
  thinkingContent,
  isThinking,
  durationSeconds,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the preview window during streaming
  useEffect(() => {
    if (isThinking && previewRef.current) {
      previewRef.current.scrollTop = previewRef.current.scrollHeight;
    }
  }, [thinkingContent, isThinking]);

  // Customers only see that an answer is being prepared, not the reasoning.
  const customer = usePortal().access?.accountType === "customer";

  if (!thinkingContent && !isThinking) return null;
  if (customer)
    return isThinking ? (
      <div className="thinking-block">
        <div className="thinking-block-header">
          <span className="thinking-block-label">Thinking…</span>
          <span className="thinking-block-dot-pulse" />
        </div>
      </div>
    ) : null;

  const headerLabel = isThinking
    ? "Thinking"
    : durationSeconds
      ? `Thought for ${durationSeconds}s`
      : "Thought Process";

  return (
    <div className="thinking-block">
      {/* Header row */}
      <div
        className={`thinking-block-header ${!isThinking ? "clickable" : ""}`}
        onClick={() => !isThinking && setIsExpanded(!isExpanded)}
        onKeyDown={e => {
          if (!isThinking && (e.key === "Enter" || e.key === " ")) {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <span className="thinking-block-label">{headerLabel}</span>
        {!isThinking && (
          <span
            className={`thinking-block-chevron ${isExpanded ? "expanded" : ""}`}
          >
            ›
          </span>
        )}
        {isThinking && <span className="thinking-block-dot-pulse" />}
      </div>

      {/* Preview window during streaming (3-line max) */}
      {isThinking && thinkingContent && (
        <div className="thinking-block-preview" ref={previewRef}>
          {thinkingContent}
        </div>
      )}

      {/* Full content when expanded after completion */}
      {!isThinking && isExpanded && thinkingContent && (
        <div className="thinking-block-full">{thinkingContent}</div>
      )}
    </div>
  );
};
